import React, { useMemo } from 'react';
import { isEqual } from 'lodash';
import { useNavigate } from 'react-router';
import { Button, Gutter, PillsItem, Pills } from '@axiom/ui';
import type {
  TalentSearchDisplayFilters,
  TalentSearchDisplayFiltersCategory,
  TalentSearchItem,
} from '@axiom/validation';

import { WindowUtil } from '../../utils/window-util';
import { CandidateApi } from '../../api/candidate-api';
import type { TalentSearchActiveQueryFilters } from '../../schema/talent-search-active-query-filters';

export interface TalentSearchPillsProps {
  displayFilters: TalentSearchDisplayFilters;
}

export const TalentSearchPills = ({
  displayFilters,
}: TalentSearchPillsProps) => {
  const navigate = useNavigate();
  const pillsModel = useMemo(() => {
    return displayFilters.categories
      .reduce<TalentSearchItem[]>(
        (accum, category: TalentSearchDisplayFiltersCategory) => {
          const catItems = [
            ...category.items.alwaysShow,
            ...category.items.showHide,
          ];

          return [...accum, ...catItems.filter(item => item.checked)];
        },
        []
      )
      .map(item => ({
        ...item,
        id: JSON.stringify(item.id),
      }));
  }, [displayFilters.categories]);

  const removeValues = (
    queryObject: TalentSearchActiveQueryFilters,
    valuesToRemove: string
  ) => {
    const newObj: TalentSearchActiveQueryFilters = {};
    const parsedValuesToRemove = JSON.parse(valuesToRemove);

    for (const key in queryObject) {
      if (Array.isArray(queryObject[key])) {
        const filteredArray = queryObject[key].filter(value => {
          if (
            typeof value === 'object' &&
            typeof parsedValuesToRemove === 'object'
          ) {
            return !isEqual(parsedValuesToRemove, value);
          } else if (
            typeof value === 'string' &&
            typeof parsedValuesToRemove === 'string'
          ) {
            return !parsedValuesToRemove.includes(value);
          }
          return true;
        });

        if (filteredArray.length > 0) {
          newObj[key] = filteredArray;
        }
      } else {
        newObj[key] = queryObject[key];
      }
    }
    return newObj;
  };

  return (
    <div data-test="TALENT_SEARCH_FILTER_PILLS_CONTAINER">
      {pillsModel.length > 0 ? (
        <Gutter bottom="16px">
          <Pills name="TALENT_SEARCH_FILTER_PILLS">
            {pillsModel.map(pill => (
              <PillsItem
                name="TALENT_SEARCH_FILTER_PILL"
                key={pill.id}
                pattern="secondary"
                onDismiss={async () => {
                  const query = WindowUtil.getUrlQuery();
                  const newQuery = removeValues(
                    query as TalentSearchActiveQueryFilters,
                    pill.id
                  );
                  void navigate('/browse-talent/page/1');
                  WindowUtil.setUrlQuery(newQuery, true);
                  await CandidateApi.refreshCandidateSearchResults();
                }}
              >
                {pill.name}
              </PillsItem>
            ))}
            <Button
              pattern="secondary"
              variation="minimal"
              name="TALENT_SEARCH_CLEAR_ALL"
              onClick={async () => {
                void navigate('/browse-talent/page/1');
                WindowUtil.setUrlQuery({}, true);
                await CandidateApi.refreshCandidateSearchResults();
              }}
            >
              Clear All
            </Button>
          </Pills>
        </Gutter>
      ) : null}
    </div>
  );
};
